import { z } from 'zod';
import { locationSnapshotSchema } from './location.js';
import { canonicalJsonStringify, instantSchema, metresSchema, nonEmptyTextSchema, nonnegativeSafeIntegerSchema, recordedMetresSchema, semverSchema, sha256Schema, sha256CanonicalJson, uuidSchema, } from './primitives.js';
import { artifactVersionSchema, revisionMetadataSchema } from './versioning.js';
import { vehicleSnapshotSchema } from './vehicle.js';
export const MAX_ROUTE_VIA_POINTS = 128;
export const routeCandidateSourceSchema = z.enum(['manual', 'template', 'history', 'network']);
export const distanceProvenanceKindSchema = z.enum([
    'manual',
    'trusted_import',
    'network_graph',
    'unknown',
]);
export const distanceQualitySchema = z.enum(['confirmed', 'estimated', 'unknown']);
export const routeIdentitySchema = z.strictObject({
    candidate_source: routeCandidateSourceSchema,
    destination_location_id: uuidSchema,
    graph_sha256: sha256Schema.nullable(),
    network_edge_ids: z.array(z.string().min(1).max(256)).max(50_000),
    origin_location_id: uuidSchema,
    via_location_ids: z.array(uuidSchema).max(MAX_ROUTE_VIA_POINTS),
});
export const distanceProvenanceSchema = z
    .strictObject({
    metres: recordedMetresSchema.nullable(),
    provenance: distanceProvenanceKindSchema,
    quality: distanceQualitySchema,
    calculation_basis: z.enum([
        'explicit_user_entry',
        'exact_route_import',
        'exact_confirmed_graph_edges',
        'graph_estimate',
        'none',
    ]),
    source_artifact: artifactVersionSchema.nullable(),
    source_record_id: z.string().min(1).max(512).nullable(),
    user_confirmed_at: instantSchema.nullable(),
})
    .superRefine((value, context) => {
    if (value.metres === null) {
        if (value.provenance !== 'unknown' ||
            value.quality !== 'unknown' ||
            value.calculation_basis !== 'none' ||
            value.source_artifact !== null ||
            value.source_record_id !== null ||
            value.user_confirmed_at !== null) {
            context.addIssue({
                code: 'custom',
                message: 'a missing distance must use only unknown/none provenance fields',
            });
        }
        return;
    }
    if (value.provenance === 'unknown' || value.quality === 'unknown') {
        context.addIssue({
            code: 'custom',
            message: 'a known distance requires non-unknown provenance and quality',
        });
    }
    if (value.provenance === 'manual') {
        if (value.calculation_basis !== 'explicit_user_entry' ||
            value.quality !== 'confirmed' ||
            value.user_confirmed_at === null ||
            value.source_artifact !== null ||
            value.source_record_id !== null) {
            context.addIssue({
                code: 'custom',
                message: 'manual distance requires an explicit confirmation and no source artifact',
            });
        }
    }
    if (value.provenance === 'trusted_import') {
        if (value.calculation_basis !== 'exact_route_import' ||
            value.quality !== 'confirmed' ||
            value.source_artifact === null ||
            value.source_record_id === null ||
            value.user_confirmed_at !== null) {
            context.addIssue({
                code: 'custom',
                message: 'trusted import requires an exact source artifact and record',
            });
        }
    }
    if (value.provenance === 'network_graph') {
        const exact = value.calculation_basis === 'exact_confirmed_graph_edges';
        const estimate = value.calculation_basis === 'graph_estimate';
        if ((!exact && !estimate) ||
            value.source_artifact === null ||
            value.source_record_id !== null ||
            value.user_confirmed_at !== null ||
            (exact && value.quality !== 'confirmed') ||
            (estimate && value.quality !== 'estimated')) {
            context.addIssue({
                code: 'custom',
                message: 'network distance quality must reflect exact edges versus an estimate',
            });
        }
    }
});
export const routeConfirmationSchema = z.strictObject({
    kind: z.literal('explicit_user_confirmation'),
    confirmed_at: instantSchema,
    fingerprint_contract: z.literal('dmf.route-variant-identity.sha256.v1'),
    route_fingerprint_sha256: sha256Schema,
});
export const networkSegmentSchema = z.strictObject({
    sequence: nonnegativeSafeIntegerSchema,
    graph_edge_id: z.string().min(1).max(256),
    line_reference: z.string().min(1).max(128).nullable(),
    from_location_id: uuidSchema.nullable(),
    to_location_id: uuidSchema.nullable(),
    length_metres: metresSchema,
});
export const routeUsageContextSchema = z.strictObject({
    direction_key: z.string().min(1).max(512),
    train_or_service_number: z.string().min(1).max(128).nullable(),
    work_type: z.string().min(1).max(128).nullable(),
    operator: z.string().min(1).max(256).nullable(),
    vehicle_id: uuidSchema.nullable(),
});
export const routeVariantSchema = z
    .strictObject({
    route_variant_id: uuidSchema,
    origin_location_id: uuidSchema,
    destination_location_id: uuidSchema,
    via_location_ids: z.array(uuidSchema).max(MAX_ROUTE_VIA_POINTS),
    network_segments: z.array(networkSegmentSchema).max(50_000),
    distance: distanceProvenanceSchema,
    candidate_source: routeCandidateSourceSchema,
    confirmation: routeConfirmationSchema.nullable(),
    explicit_confirmation_count: nonnegativeSafeIntegerSchema,
    use_count: nonnegativeSafeIntegerSchema,
    last_used_at: instantSchema.nullable(),
    favorite: z.boolean(),
    default_for_context: z.boolean(),
    usage_contexts: z.array(routeUsageContextSchema).max(512),
    catalog_version: artifactVersionSchema.nullable(),
    graph_version: artifactVersionSchema.nullable(),
    route_scoring_version: semverSchema,
    metadata: revisionMetadataSchema,
})
    .superRefine((value, context) => {
    validateRouteShape(value, context);
    if (value.explicit_confirmation_count > value.use_count) {
        context.addIssue({
            code: 'custom',
            path: ['explicit_confirmation_count'],
            message: 'explicit confirmations cannot exceed total use count',
        });
    }
    if ((value.use_count === 0) !== (value.last_used_at === null)) {
        context.addIssue({
            code: 'custom',
            path: ['last_used_at'],
            message: 'last_used_at must be present exactly when the route has been used',
        });
    }
    if (value.explicit_confirmation_count > 0 !== (value.confirmation !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['confirmation'],
            message: 'confirmation evidence must match whether the route has confirmations',
        });
    }
    if (value.confirmation) {
        validateRouteConfirmation(value.confirmation, {
            candidate_source: value.candidate_source,
            destination_location_id: value.destination_location_id,
            graph_sha256: value.graph_version?.sha256 ?? null,
            network_edge_ids: value.network_segments.map((segment) => segment.graph_edge_id),
            origin_location_id: value.origin_location_id,
            via_location_ids: value.via_location_ids,
        }, context);
    }
    validateGraphDistance(value.distance, value.graph_version, context);
});
export const routeSnapshotSchema = z
    .strictObject({
    snapshot_version: z.literal('route_snapshot.v1'),
    route_variant_id: uuidSchema,
    captured_at: instantSchema,
    origin: locationSnapshotSchema,
    via: z.array(locationSnapshotSchema).max(MAX_ROUTE_VIA_POINTS),
    destination: locationSnapshotSchema,
    network_segments: z.array(networkSegmentSchema).max(50_000),
    distance: distanceProvenanceSchema,
    candidate_source: routeCandidateSourceSchema,
    confirmation: routeConfirmationSchema,
    catalog_version: artifactVersionSchema.nullable(),
    graph_version: artifactVersionSchema.nullable(),
    route_scoring_version: semverSchema,
})
    .superRefine((value, context) => {
    validateRouteShape(value, context);
    validateRouteConfirmation(value.confirmation, {
        candidate_source: value.candidate_source,
        destination_location_id: value.destination.location_id,
        graph_sha256: value.graph_version?.sha256 ?? null,
        network_edge_ids: value.network_segments.map((segment) => segment.graph_edge_id),
        origin_location_id: value.origin.location_id,
        via_location_ids: value.via.map((location) => location.location_id),
    }, context);
    validateGraphDistance(value.distance, value.graph_version, context);
});
/**
 * Reusable route data carried by an editor draft or a standalone template.
 * Explicit confirmation is deliberately excluded: applying a remembered route
 * must always produce a new confirmation bound to its effective candidate
 * source and exact graph identity.
 */
export const routeSeedSchema = z
    .strictObject({
    candidate_source: routeCandidateSourceSchema,
    via: z.array(locationSnapshotSchema).max(MAX_ROUTE_VIA_POINTS),
    network_segments: z.array(networkSegmentSchema).max(50_000),
    distance: distanceProvenanceSchema,
    catalog_version: artifactVersionSchema.nullable(),
    graph_version: artifactVersionSchema.nullable(),
    route_scoring_version: semverSchema,
})
    .superRefine((value, context) => {
    validateRouteShape(value, context);
    validateGraphDistance(value.distance, value.graph_version, context);
});
export const routeTemplateSourceSnapshotSchema = z.strictObject({
    snapshot_version: z.literal('route_template_source_snapshot.v1'),
    source_route_variant_id: uuidSchema,
    captured_at: instantSchema,
    origin: locationSnapshotSchema,
    destination: locationSnapshotSchema,
    route_seed: routeSeedSchema,
    vehicles: z.array(vehicleSnapshotSchema).max(16),
});
export const routeTemplateSchema = z
    .strictObject({
    route_template_id: uuidSchema,
    name: nonEmptyTextSchema,
    origin_location_id: uuidSchema,
    destination_location_id: uuidSchema,
    via_location_ids: z.array(uuidSchema).max(MAX_ROUTE_VIA_POINTS),
    preferred_route_variant_id: uuidSchema.nullable(),
    default_train_or_service_number: z.string().min(1).max(128).nullable(),
    default_work_type: z.string().min(1).max(128).nullable(),
    default_operator: z.string().min(1).max(256).nullable(),
    default_vehicle_ids: z.array(uuidSchema).max(16),
    favorite: z.boolean(),
    use_count: nonnegativeSafeIntegerSchema,
    /** Missing only on legacy shallow templates, which remain readable but unusable. */
    source_snapshot: routeTemplateSourceSnapshotSchema.exactOptional(),
    metadata: revisionMetadataSchema,
})
    .superRefine((value, context) => {
    const snapshot = value.source_snapshot;
    if (snapshot === undefined)
        return;
    if (value.preferred_route_variant_id !== snapshot.source_route_variant_id) {
        context.addIssue({
            code: 'custom',
            path: ['preferred_route_variant_id'],
            message: 'standalone template must retain its exact source route variant identity',
        });
    }
    if (snapshot.origin.location_id !== value.origin_location_id) {
        context.addIssue({
            code: 'custom',
            path: ['source_snapshot', 'origin', 'location_id'],
            message: 'template source snapshot must retain its origin identity',
        });
    }
    if (snapshot.destination.location_id !== value.destination_location_id) {
        context.addIssue({
            code: 'custom',
            path: ['source_snapshot', 'destination', 'location_id'],
            message: 'template source snapshot must retain its destination identity',
        });
    }
    const snapshotViaIds = snapshot.route_seed.via.map((location) => location.location_id);
    if (canonicalJsonStringify(snapshotViaIds) !== canonicalJsonStringify(value.via_location_ids)) {
        context.addIssue({
            code: 'custom',
            path: ['source_snapshot', 'route_seed', 'via'],
            message: 'template source snapshot must retain its ordered via identities',
        });
    }
    const vehicleIds = new Set(snapshot.vehicles.map((vehicle) => vehicle.vehicle_id));
    if (vehicleIds.size !== snapshot.vehicles.length) {
        context.addIssue({
            code: 'custom',
            path: ['source_snapshot', 'vehicles'],
            message: 'template source vehicle snapshots must have unique identities',
        });
    }
    if (new Set(value.default_vehicle_ids).size !== value.default_vehicle_ids.length) {
        context.addIssue({
            code: 'custom',
            path: ['default_vehicle_ids'],
            message: 'template default vehicle identities must be unique',
        });
    }
    if (value.default_vehicle_ids.some((vehicleId) => !vehicleIds.has(vehicleId))) {
        context.addIssue({
            code: 'custom',
            path: ['default_vehicle_ids'],
            message: 'template default vehicles must exist in its standalone source snapshot',
        });
    }
});
/** Legacy templates may carry a once-valid or foreign work-type string. */
export function canonicalRouteTemplateWorkType(value) {
    switch (value) {
        case 'passenger':
        case 'freight':
        case 'shunting':
        case 'light_engine':
        case 'service_run':
        case 'other':
            return value;
        case null:
        default:
            return null;
    }
}
export function routeIdentityCanonicalJson(input) {
    const parsed = routeIdentitySchema.parse(input);
    return canonicalJsonStringify(parsed);
}
export function routeFingerprintSha256(input) {
    const identity = routeIdentitySchema.parse(input);
    return sha256CanonicalJson('dmf.route-variant-identity.sha256.v1', identity);
}
function validateNetworkSegments(segments, context, basePath) {
    segments.forEach((segment, index) => {
        if (segment.sequence !== index) {
            context.addIssue({
                code: 'custom',
                path: [...basePath, index, 'sequence'],
                message: 'network segment sequence must be contiguous and zero-based',
            });
        }
    });
}
function validateRouteShape(value, context) {
    validateNetworkSegments(value.network_segments, context, ['network_segments']);
    const graphRequired = value.candidate_source === 'network' ||
        value.network_segments.length > 0 ||
        value.distance.provenance === 'network_graph';
    if (graphRequired && value.graph_version === null) {
        context.addIssue({
            code: 'custom',
            path: ['graph_version'],
            message: 'graph_version is required for a network source, edge list, or graph distance',
        });
    }
    if (value.graph_version !== null &&
        value.candidate_source === 'manual' &&
        value.network_segments.length === 0 &&
        value.distance.provenance !== 'network_graph') {
        context.addIssue({
            code: 'custom',
            path: ['graph_version'],
            message: 'an unseeded manual route cannot claim a graph artifact',
        });
    }
}
function validateRouteConfirmation(confirmation, identity, context) {
    const expectedFingerprint = routeFingerprintSha256(identity);
    if (confirmation.route_fingerprint_sha256 !== expectedFingerprint) {
        context.addIssue({
            code: 'custom',
            path: ['confirmation', 'route_fingerprint_sha256'],
            message: 'route confirmation fingerprint must bind the exact ordered canonical route identity',
        });
    }
}
function validateGraphDistance(distance, graphVersion, context) {
    if (distance.provenance === 'network_graph') {
        if (graphVersion === null || distance.source_artifact?.sha256 !== graphVersion.sha256) {
            context.addIssue({
                code: 'custom',
                path: ['distance', 'source_artifact'],
                message: 'network distance must reference the exact route graph artifact',
            });
        }
    }
}
//# sourceMappingURL=route.js.map